import React, { useState } from "react";
import "./DailyPuzzle.css";
import { Seo } from "../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../Landing/IntroParagraphs.tsx";
import { canonicalUrl, findBySlug } from "../Shared/Seo/routeSeo.ts";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import { format } from "../Shared/i18n/format.ts";
import { EqualGroupsBoard } from "./EqualGroupsBoard.tsx";
import { LEVELS, Level, gradeOf, localDateKey } from "./equalGroups.ts";
import { readLevel, saveLevel } from "./store.ts";

interface DailyPuzzlePageProps {
    slug: string;
}

/**
 * The Daily Math Puzzle page (ADR 0127): a keyword landing page around Equal
 * Groups, with a button for each of grades 2 to 8 (ADR 0127 §9). Its route carries `dailyPuzzle`, not
 * `quiz`, so it has no queue, picker, settings or missed facts to wire.
 *
 * The date is read once, when the page opens: a tab left open past midnight keeps
 * the board it began with until it is reloaded.
 */
export function DailyPuzzlePage({ slug }: DailyPuzzlePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [level, setLevel] = useState<Level>(readLevel);
    const [date] = useState(() => localDateKey(new Date()));

    function choose(next: Level) {
        setLevel(next);
        saveLevel(next);
    }

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="dailyPuzzleTool">
                <div className="dpLevels" role="group" aria-label={t("dailyPuzzle.levelsLabel")}>
                    {LEVELS.map((option) => (
                        <button
                            key={option}
                            type="button"
                            className="dpLevel"
                            aria-pressed={option === level}
                            onClick={() => choose(option)}
                        >
                            {format(t("dailyPuzzle.gradeTemplate"), { n: gradeOf(option) })}
                        </button>
                    ))}
                </div>
                <EqualGroupsBoard key={level} level={level} date={date} url={canonicalUrl(entry.slug)} />
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default DailyPuzzlePage;
